import styled from "styled-components";
import { CSSVars } from "../../styles/styles.config";

// Visual language borrowed from the live site, not the mockup: the /widgets and
// homepage hero (hero-bg.webp, light 300-weight title, pink to blue gradient
// phrase), the blog category pills, and the homepage Popular Widgets hover.
const brandGradient = "linear-gradient(90deg, #ff979c 0%, #8e90ff 100%)";

export const StyledWidgetsLibraryV2 = styled.div`
  font-family: ${CSSVars.primaryFont};
  color: ${CSSVars.black};
  background: ${CSSVars.white};

  button {
    font-family: inherit;
    cursor: pointer;
  }

  .gradient {
    background: ${brandGradient};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    white-space: nowrap;
  }

  /* Hero */
  .hero {
    position: relative;
    /* Above the sticky chips (10), or the search suggestions, which live
       inside this stacking context, slide under the chips and logos. */
    z-index: 11;
    color: ${CSSVars.white};
    text-align: center;
    background: url(/assets/hero-bg.webp);
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  /* Platform pages: the platform's own logo on a white tile, in place of
     the avatar pill, and a little closer to the headline. */
  .platform-badge {
    display: flex;
    justify-content: center;
    margin-bottom: 22px;
  }

  /* The platform icons in Mongo are white, drawn for a dark hero, so the
     tile is frosted glass rather than white. No border (Sergei, 09-29). */
  .platform-logo {
    position: relative;
    display: block;
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(6px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    overflow: hidden;

    img {
      object-fit: contain;
      padding: 16px;
    }
  }

  /* The same icon, very large and faint, on the left of the hero: the old
     platform hero's watermark, turned down to match the main page marks. */
  .platform-watermark {
    position: absolute;
    left: -4%;
    top: 50%;
    width: min(46vw, 520px);
    aspect-ratio: 1;
    transform: translateY(-50%);
    opacity: 0.06;
    pointer-events: none;

    img {
      object-fit: contain;
    }

    @media (max-width: 767px) {
      opacity: 0.04;
    }
  }

  .hero-marks {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;

    svg {
      position: absolute;
      fill: ${CSSVars.white};
      opacity: 0.07;
    }

    /* Phones: the text spans the full width, so keep the marks fainter */
    @media (max-width: 767px) {
      svg {
        opacity: 0.045;
      }
    }
  }

  .hero-inner {
    position: relative;
    max-width: 1240px;
    margin: 0 auto;
    padding: 96px 20px 40px;

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      padding: 120px 40px 48px;
    }
  }

  .hero-proof {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;

    .rating-wrapper {
      width: auto;
      max-width: none;
    }

    p {
      color: ${CSSVars.white};
    }
  }

  h1 {
    margin: 0 auto;
    font-weight: 300;
    /* +2px on tablets and up (Sergei, 2026-09-29): 60px on laptops. Phones
       stay at 32px, where 34px pushes "zero code." onto a third line. */
    font-size: clamp(32px, calc(4.3vw + 2px), 60px);
    line-height: 1.12;
    letter-spacing: -0.02em;

    .gradient {
      font-weight: 400;
    }

    /* Always two lines: the promise, then the two proof points. */
    .second-line {
      display: block;
    }
  }

  .lead {
    max-width: 760px;
    margin: 20px auto 28px;
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 300;
    line-height: 1.6;
    opacity: 0.85;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSizeLG};
    }
  }

  .search {
    position: relative;
    max-width: 640px;
    margin: 0 auto;
    text-align: left;

    .search-icon {
      position: absolute;
      left: 20px;
      top: 29px;
      transform: translateY(-50%);
      font-size: 20px;
      color: ${CSSVars.gray500};
      pointer-events: none;
    }

    input {
      width: 100%;
      height: 58px;
      padding: 0 120px 0 52px;
      border: 1px solid transparent;
      border-radius: 999px;
      outline: none;
      font-family: inherit;
      font-size: 16px;
      color: ${CSSVars.black};
      background: ${CSSVars.white};
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);

      &:focus-visible {
        border-color: ${CSSVars.blue500};
        box-shadow: 0 0 0 4px rgba(91, 91, 255, 0.35), 0 16px 40px rgba(0, 0, 0, 0.3);
      }

      &::placeholder {
        color: ${CSSVars.gray500};
        font-weight: 300;
      }

      @media (max-width: 480px) {
        padding-right: 20px;
        font-size: 15px;
      }
    }

    > button {
      position: absolute;
      right: 6px;
      top: 6px;
      height: 46px;
      padding: 0 24px;
      border: 0;
      border-radius: 999px;
      color: ${CSSVars.white};
      background: ${CSSVars.blue500};
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 500;
      transition: background 0.2s;

      &:hover {
        background: linear-gradient(270deg, #ff979c 0%, ${CSSVars.blue500} 100%);
      }

      @media (max-width: 480px) {
        display: none;
      }
    }
  }

  .suggest-panel {
    position: absolute;
    z-index: 20;
    left: 0;
    right: 0;
    top: 66px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 18px;
    background: ${CSSVars.white};
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
    text-align: left;
  }

  .suggest-all {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    border: 0;
    border-top: 1px solid ${CSSVars.gray100};
    background: ${CSSVars.white};
    color: ${CSSVars.blue500};
    font-family: inherit;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 500;
    cursor: pointer;

    &:hover {
      background: ${CSSVars.blue50};
    }
  }

  .suggestions {
    /* About five rows, then the list scrolls on its own */
    max-height: min(300px, 45vh);
    overflow-y: auto;
    overscroll-behavior: contain;
    margin: 0;
    padding: 8px;
    list-style: none;

    a {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 12px;
      border-radius: 12px;
      color: ${CSSVars.black};

      img {
        border-radius: 7px;
      }

      strong {
        display: block;
        font-size: ${CSSVars.fontSizeSM};
        font-weight: 500;
      }

      small {
        display: block;
        color: ${CSSVars.gray500};
        font-size: ${CSSVars.fontSizeXS};
        font-weight: 300;
      }
    }

    li[aria-selected="true"] a,
    a:hover {
      background: ${CSSVars.blue50};
    }
  }

  .quick {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 6px 14px;
    margin-top: 16px;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 300;

    > span {
      opacity: 0.6;
    }

    button {
      padding: 0;
      border: 0;
      background: transparent;
      color: ${CSSVars.white};
      font-size: inherit;
      font-weight: 400;
      text-decoration: underline;
      text-decoration-color: rgba(255, 255, 255, 0.35);
      text-underline-offset: 4px;

      &:hover {
        text-decoration-color: #ff979c;
      }
    }
  }

  .scores {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 6px 10px;
    margin-top: 26px;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 300;

    .star {
      color: #ffc24d;
    }

    strong {
      font-weight: 600;
    }

    .dot {
      opacity: 0.5;
    }
  }

  /* Category chips: real links, sticky while you browse */
  .chips {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid ${CSSVars.gray100};
    /* top is set inline to sit under the floating site header; it moves
       with the header's own 0.3s slide. */
    transition: top 0.3s ease, background 0.25s ease, box-shadow 0.25s ease;

    /* Once it floats over the cards: a soft lavender tint, a shadow and a
       brand-gradient hairline, so it reads as a bar and not as more content.
       The pills turn solid white to stand off the tint. */
    &.is-stuck {
      background: rgba(247, 246, 255, 0.94);
      border-bottom-color: transparent;
      box-shadow: 0 8px 24px rgba(76, 79, 255, 0.12);

      &:after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background: ${brandGradient};
        opacity: 0.7;
      }

      .chip:not(.active) {
        border-color: #e3e3f5;
        background: ${CSSVars.white};
        box-shadow: 0 1px 3px rgba(22, 22, 42, 0.06);
      }
    }
  }

  .chips-track {
    display: flex;
    gap: 6px;
    max-width: 1440px;
    margin: 0 auto;
    padding: 14px 20px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @media screen and (min-width: ${CSSVars.breakpointXL}) {
      flex-wrap: wrap;
      justify-content: center;
      padding: 16px 24px;
    }
  }

  .chip {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px 6px 9px;
    border-radius: 999px;
    border: 1px solid ${CSSVars.gray100};
    color: ${CSSVars.gray600};
    background: ${CSSVars.white};
    /* Nine chips fit one row from about 1300px wide */
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    transition: 0.2s;

    /* Design B: a line icon in the brand lavender, and the brand gradient
       on the selected chip. No counts, so small groups do not look thin. */
    svg {
      flex-shrink: 0;
      width: 15px;
      height: 15px;
      color: #8e90ff;
      transition: color 0.2s;
    }

    &:hover {
      border-color: #8e90ff;
      color: ${CSSVars.black};
    }

    &.active {
      color: ${CSSVars.white};
      background: ${brandGradient} border-box;
      border-color: transparent;
      box-shadow: 0 4px 14px rgba(142, 144, 255, 0.3);

      svg {
        color: ${CSSVars.white};
      }
    }
  }

  /* Main */
  .main {
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px 20px 60px;

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      padding: 56px 40px 80px;
    }
  }

  .row + .row {
    margin-top: 56px;
  }

  #all-widgets {
    scroll-margin-top: 80px;
  }

  .row-head {
    margin-bottom: 22px;

    h2 {
      margin: 0 0 6px;
      font-size: ${CSSVars.fontSizeXL};
      font-weight: 600;
      line-height: 1.25;

      @media screen and (min-width: ${CSSVars.breakpointLG}) {
        font-size: ${CSSVars.fontSize2XL};
      }
    }

    p {
      margin: 0;
      color: ${CSSVars.gray500};
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 300;
    }
  }

  .row-head-split {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }

  .clear {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 7px 14px;
    border: 1px solid ${CSSVars.gray100};
    border-radius: 999px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};

    &:hover {
      border-color: ${CSSVars.blue500};
      color: ${CSSVars.blue500};
    }
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media screen and (min-width: ${CSSVars.breakpointSM}) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }

    @media screen and (min-width: ${CSSVars.breakpointXL}) {
      grid-template-columns: repeat(4, 1fr);
    }

    /* Cards past the current page stay in the HTML as crawlable links. */
    > .is-hidden {
      display: none;
    }

    > div {
      display: flex;
    }
  }

  /* Card: keeps the illustrated catalog previews, our edge over icon-only grids */
  .card {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    border: 1px solid ${CSSVars.gray100};
    border-radius: 16px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: ${CSSVars.blue500};
      transform: translateY(-3px);
      box-shadow: 0 10px 28px rgba(76, 79, 255, 0.14);

      .card-preview img {
        transform: scale(1.03);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;

      &:hover {
        transform: none;

        .card-preview img {
          transform: none;
        }
      }
    }
  }

  .card-preview {
    position: relative;
    display: block;
    aspect-ratio: 320 / 170;
    overflow: hidden;
    background: ${CSSVars.gray50};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
  }

  .badge {
    position: absolute;
    top: 10px;
    left: 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    border-radius: 999px;
    color: ${CSSVars.black};
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 2px 8px rgba(1, 1, 1, 0.12);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.01em;

    &:before {
      content: "";
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${CSSVars.pink500};
    }

    &.badge-trending:before {
      background: ${CSSVars.blue500};
    }

    /* New stands apart from Popular and Trending: a filled brand-gradient
       pill with white text, instead of a white pill with a dot. */
    &.badge-new {
      padding: 4px 11px;
      color: ${CSSVars.white};
      background: linear-gradient(90deg, #ff7f86 0%, #7b7dff 100%);
      box-shadow: 0 3px 10px rgba(123, 125, 255, 0.35);
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;

      &:before {
        content: "✦";
        width: auto;
        height: auto;
        border-radius: 0;
        background: none;
        font-size: 10px;
      }
    }
  }

  /* A gradient hairline around the whole card, so a new widget is spotted
     in the grid as well as in the Recently added row. */
  .card.card-new {
    border-color: transparent;
    background:
      linear-gradient(${CSSVars.white}, ${CSSVars.white}) padding-box,
      ${brandGradient} border-box;
  }

  .card-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px 18px 18px;
  }

  .card-title {
    display: flex;
    align-items: center;
    gap: 10px;

    img {
      flex-shrink: 0;
      border-radius: 7px;
    }

    strong {
      display: block;
      font-size: 15px;

      .for {
        font-weight: 400;
        color: ${CSSVars.gray500};
      }

      font-weight: 500;
      line-height: 1.3;
    }

    small {
      display: block;
      color: ${CSSVars.gray500};
      font-size: 12px;
      font-weight: 300;
    }
  }

  .card-desc {
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 300;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .more {
    display: flex;
    justify-content: center;
    margin-top: 32px;

    button {
      padding: 13px 30px;
      border: 1px solid ${CSSVars.black};
      border-radius: 999px;
      color: ${CSSVars.black};
      background: ${CSSVars.white};
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 500;
      transition: 0.2s;

      &:hover {
        color: ${CSSVars.white};
        background: ${CSSVars.black};
      }
    }
  }

  .empty {
    padding: 36px 20px;
    border: 1px dashed ${CSSVars.gray200};
    border-radius: 16px;
    text-align: center;

    h3 {
      margin: 0 0 6px;
      font-size: ${CSSVars.fontSizeXL};
      font-weight: 600;
    }

    > p {
      margin: 0 0 18px;
      color: ${CSSVars.gray500};
      font-weight: 300;
    }

    .grid {
      margin-top: 24px;
      text-align: left;
    }
  }

  .empty-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .empty-embd {
    margin: 24px 0 0 !important;

    a {
      color: ${CSSVars.blue500};
      font-weight: 500;
    }
  }

  .platform-intro {
    max-width: 820px;
    margin: 72px auto 0;
    text-align: center;

    h2 {
      margin: 0 0 14px;
      font-size: clamp(24px, 3vw, 34px);
      font-weight: 300;
    }

    p {
      margin: 0;
      color: ${CSSVars.gray600};
      font-weight: 300;
      line-height: 1.7;
      white-space: pre-line;
    }
  }

  /* How it works: a light band in the brand pink and blue tints */
  .steps {
    margin: 80px -20px 0;
    padding: 56px 20px;
    text-align: center;
    background: linear-gradient(120deg, ${CSSVars.pink50} 0%, ${CSSVars.blue50} 100%);

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      margin: 96px 0 0;
      padding: 64px 48px;
      border-radius: 24px;
    }

    h2 {
      margin: 0 0 32px;
      font-size: clamp(28px, 4vw, 44px);
      font-weight: 300;
    }

    ol {
      display: grid;
      grid-template-columns: 1fr;
      gap: 16px;
      max-width: 1000px;
      margin: 0 auto;
      padding: 0;
      list-style: none;
      text-align: left;

      @media screen and (min-width: ${CSSVars.breakpointMD}) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    li {
      padding: 22px;
      border: 1px solid rgba(255, 255, 255, 0.9);
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.75);
      box-shadow: 0 6px 20px rgba(76, 79, 255, 0.06);

      strong {
        display: block;
        margin: 14px 0 6px;
        font-size: ${CSSVars.fontSizeLG};
        font-weight: 500;
      }

      p {
        margin: 0;
        color: ${CSSVars.gray600};
        font-size: ${CSSVars.fontSizeSM};
        font-weight: 300;
        line-height: 1.6;
      }
    }
  }

  .step-num {
    display: inline-grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    color: ${CSSVars.white};
    background: linear-gradient(135deg, #ff979c 0%, ${CSSVars.blue500} 100%);
    font-weight: 600;
  }

  .steps-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 32px;
    padding: 14px 32px;
    border-radius: 999px;
    color: ${CSSVars.white};
    background: ${CSSVars.blue500};
    font-weight: 500;
    transition: 0.2s;

    &:hover {
      background: linear-gradient(270deg, #ff979c 0%, ${CSSVars.blue500} 100%);
    }
  }

  .steps-note {
    margin: 12px 0 0;
    color: ${CSSVars.gray500};
    font-size: 12px;
    font-weight: 300;
  }

  .faq-wrap {
    max-width: 900px;
    margin: 40px auto 0;
  }

  /* Phones: curated rows swipe sideways, the full list goes two-up and
     compact, so the page is not one very long single column. */
  @media (max-width: 767px) {
    .grid-scroll {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      gap: 12px;
      margin: 0 -20px;
      padding: 2px 20px 10px;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      > .card {
        flex: 0 0 74%;
        max-width: 300px;
        scroll-snap-align: start;
      }
    }

    .grid-compact {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;

      .card-body {
        padding: 10px 12px 12px;
      }

      .card-title {
        gap: 8px;

        img {
          width: 22px;
          height: 22px;
        }

        strong {
          font-size: 13px;
        }

        small {
          display: none;
        }
      }

      .card-desc {
        display: none;
      }

      .badge {
        top: 6px;
        left: 6px;
        padding: 2px 8px;
        font-size: 10px;
      }
    }
  }
`;
